const pickString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return '';
};

const asArray = (value, keys = []) => {
  if (Array.isArray(value)) {
    return value;
  }

  for (const key of keys) {
    const candidate = value?.[key];
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
};

export const normalizeNetworkDevice = (device) => ({
  id: device?.id,
  ipAddress: pickString(device?.ipAddress, device?.ip_address),
  macAddress: pickString(device?.macAddress, device?.mac_address),
  hostname: pickString(device?.hostname, device?.name),
  manufacturer: pickString(device?.manufacturer, device?.manufacturer_oui),
  deviceType: pickString(device?.deviceType, device?.device_type, 'unknown'),
  status: pickString(device?.status, 'unknown'),
  approved: Boolean(device?.isApproved ?? device?.is_approved),
  critical: Boolean(device?.isCritical ?? device?.is_critical),
  managementState: pickString(device?.managementState, device?.management_state, 'unmanaged'),
  healthState: pickString(device?.healthState, device?.health_state),
  onboardingState: pickString(device?.onboardingState, device?.onboarding_state),
  verificationResult: pickString(device?.verificationResult, device?.verification_result),
  identityBinding: pickString(device?.identityBinding, device?.identity_binding),
  addressingMode: pickString(device?.addressingMode, device?.addressing_mode),
  recommendedAddressingMode: pickString(device?.recommendedAddressingMode, device?.recommended_addressing_mode),
  addressingApplyVia: pickString(device?.addressingApplyVia, device?.addressing_apply_via),
  preferredIpAddress: pickString(device?.preferredIpAddress, device?.preferred_ip_address),
  allowFloatingIp: Boolean(device?.allowFloatingIp ?? device?.allow_floating_ip),
  stableAddressRequired: Boolean(device?.stableAddressRequired ?? device?.stable_address_required),
  firstSeen: pickString(device?.firstSeen, device?.first_seen),
  lastSeen: pickString(device?.lastSeen, device?.last_seen),
  notes: pickString(device?.notes),
  baseline: device?.rttBaselineMs ?? device?.rtt_baseline_ms,
  interfaces: asArray(device, ['interfaces']).map((entry) => ({
    id: entry?.id,
    addressType: pickString(entry?.addressType, entry?.address_type, 'interface'),
    ipAddress: pickString(entry?.ipAddress, entry?.ip_address),
    macAddress: pickString(entry?.macAddress, entry?.mac_address),
    interfaceName: pickString(entry?.interfaceName, entry?.interface_name, 'primary'),
    isPrimary: Boolean(entry?.isPrimary ?? entry?.is_primary),
    firstSeen: pickString(entry?.firstSeen, entry?.first_seen),
    lastSeen: pickString(entry?.lastSeen, entry?.last_seen),
  })),
  openPorts: asArray(device?.openPorts ?? device?.open_ports).map((entry) => ({
    port: Number(entry?.port),
    protocol: pickString(entry?.protocol),
    service: pickString(entry?.service),
    description: pickString(entry?.description),
  })),
  details: device?.identificationDetails ?? device?.identification_details ?? {},
});

export const normalizeNetworkInterfaceAccountReport = (report) => ({
  queriedAt: pickString(report?.queriedAt, report?.queried_at),
  integrityStatus: pickString(report?.integrityStatus, report?.integrity_status, 'unknown'),
  warningCode: pickString(report?.warningCode, report?.warning_code),
  liveCheckError: pickString(report?.liveCheckError, report?.live_check_error),
  anonymousAccessEnabled: Boolean(report?.anonymousAccessEnabled ?? report?.anonymous_access_enabled),
  approvedAccounts: asArray(report, ['approvedAccounts', 'approved_accounts']).map((entry) => String(entry)),
  unknownAccounts: asArray(report, ['unknownAccounts', 'unknown_accounts']).map((entry) => String(entry)),
  users: asArray(report, ['users']).map((entry) => ({
    username: pickString(entry?.username),
    userLevel: pickString(entry?.userLevel, entry?.user_level),
  })),
});

export const normalizeManagedNetworkInterface = (entry) => ({
  id: entry?.id,
  type: pickString(entry?.type, 'unknown'),
  label: pickString(entry?.label, entry?.interfaceName, entry?.interface_name, 'Managed interface'),
  baseUrl: pickString(entry?.baseUrl, entry?.base_url),
  deviceId: pickString(entry?.deviceId, entry?.device_id),
  port: entry?.port ? Number(entry.port) : null,
  enabled: Boolean(entry?.enabled ?? true),
  credentialState: pickString(entry?.credentialState, entry?.credential_state),
  lastTestAt: pickString(entry?.lastTestAt, entry?.last_test_at),
  lastTestResult: pickString(entry?.lastTestResult, entry?.last_test_result),
  lastHealthAt: pickString(entry?.lastHealthAt, entry?.last_health_at),
  healthState: pickString(entry?.healthState, entry?.health_state),
  tlsVerify: Boolean(entry?.tlsVerify ?? entry?.tls_verify),
  notes: pickString(entry?.notes),
});

export const getNetworkDeviceDisplayStatus = (device) => {
  const hasManagedSignal = device?.managementState === 'managed' || Boolean(pickString(device?.healthState));

  if (hasManagedSignal) {
    return pickString(device?.healthState, 'degraded').toLowerCase();
  }

  return pickString(device?.status, 'unknown').toLowerCase();
};